import { useCallback, useRef, useState } from "react";
import { logError } from "../../../utils/customLogger";
import { fetchNycbsExternalPushTrail } from "../apis/fetchNycbsExternalPushTrail";
import { SftpPushTrailRow } from "../types/sftpPushTrail";

export interface LoadTrailParams {
  inputFilename: string;
  caseSeq?: string | null;
  identifier?: string | null;
}

export interface UseNycbsExternalLogsTrail {
  /** True while a trail fetch is in flight. */
  isLoading: boolean;
  /** Pushes for the case last loaded, oldest first. */
  rows: SftpPushTrailRow[];
  loadTrail: (params: LoadTrailParams) => Promise<void>;
  reset: () => void;
}

/**
 * Loads one case's SFTP push trail for the Logs modal.
 *
 * Keeps the fetch out of the already-large table component. A failed read
 * (including the table not existing in an environment yet) resolves to no rows
 * and is logged, so the modal degrades to its empty state instead of breaking
 * the page.
 *
 * @input orgId - BigQuery org id the table is scoped to
 * @return UseNycbsExternalLogsTrail: Trail rows, loading flag, and loaders
 */
export const useNycbsExternalLogsTrail = (
  orgId: string,
): UseNycbsExternalLogsTrail => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<SftpPushTrailRow[]>([]);
  // Only the newest request may write state: reopening the modal on another row
  // while a fetch is in flight must not be overwritten by the stale response.
  const latestRequestRef = useRef(0);

  const loadTrail = useCallback(
    async (params: LoadTrailParams) => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;

      setIsLoading(true);
      setRows([]);

      try {
        const trailRows = await fetchNycbsExternalPushTrail({
          orgId,
          inputFilename: params.inputFilename,
          caseSeq: params.caseSeq,
          identifier: params.identifier,
        });

        if (latestRequestRef.current !== requestId) {
          return;
        }
        setRows(trailRows);
      } catch (error) {
        logError(error as Error, "Error loading NYCBS External push trail");

        if (latestRequestRef.current !== requestId) {
          return;
        }
        setRows([]);
      } finally {
        if (latestRequestRef.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    [orgId],
  );

  const reset = useCallback(() => {
    latestRequestRef.current += 1;
    setIsLoading(false);
    setRows([]);
  }, []);

  return { isLoading, rows, loadTrail, reset };
};
